import { useCallback, useEffect, useState } from 'react';
import ModalAside from 'App/components/lab/ModalAside';
import { B2B_WHOLESALE } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import {
  CustomersFiltersType,
  defaultFilters,
} from 'domains/Customers/Customers/customersServices';
import ModalContent from 'domains/Customers/Customers/pages/CustomerListPage/components/FilterModal/components/ModalContent';
import ModalFooter from 'domains/Customers/Customers/pages/CustomerListPage/components/FilterModal/components/ModalFooter';
import { useTranslationCustomers } from 'domains/Customers/hooks';
import { trackingCustomerFilter } from 'domains/Customers/tracking';
import { useGetCustomerMetafields } from 'domains/Metafields/hooks';
import { useAdvancedCustomerFilter, useCustomerResults } from '../../hooks';

interface FilterModalProps {
  show: boolean;
  closeModal: () => void;
  filters: CustomersFiltersType;
  getCustomers: (customFilters: CustomersFiltersType) => void;
  advancedSearchCustomers: (customFilters: CustomersFiltersType) => void;
}

export default function FilterModal({
  show,
  closeModal,
  filters,
  getCustomers,
  advancedSearchCustomers,
}: Readonly<FilterModalProps>) {
  const [hasCalledApplyFilters, setHasCalledApplyFilters] =
    useState<boolean>(false);
  const t = useTranslationCustomers();
  const { hasInternals, hasExternals } = useGetCustomerMetafields();
  const {
    values,
    selecteds,
    handleChangeMetafields,
    setAllValues,
    handleChange,
  } = useAdvancedCustomerFilter(filters);
  const { pagination, isRefreshing, isRefreshError } = useCustomerResults();

  const [hasFilterWholesaleTag] = useHasTags(B2B_WHOLESALE);

  useEffect(() => {
    if (hasCalledApplyFilters && !isRefreshing) {
      trackingCustomerFilter(pagination.totalResults, isRefreshError);
      setHasCalledApplyFilters(false);
    }
  }, [
    hasCalledApplyFilters,
    isRefreshing,
    isRefreshError,
    pagination.totalResults,
  ]);

  const handleApply = useCallback(() => {
    const hasMetafieldsFilters = Object.keys(values).some((value) =>
      value.startsWith('mf-'),
    );
    const hasPriceTableFilter = values.hasPriceTable !== '';

    setHasCalledApplyFilters(true);
    if (hasMetafieldsFilters || hasPriceTableFilter) {
      advancedSearchCustomers({
        ...values,
      });
    } else {
      getCustomers({ ...values });
    }
    closeModal();
  }, [
    values,
    closeModal,
    advancedSearchCustomers,
    getCustomers,
    setHasCalledApplyFilters,
  ]);

  const handleReset = () => {
    setAllValues(defaultFilters);
  };

  const hasMetafields = hasInternals || hasExternals;
  const hasOnlyWholesale = hasFilterWholesaleTag && !hasMetafields;

  return (
    <ModalAside
      isOpen={show}
      onDidDismiss={closeModal}
      headerContent={t('customerListPage.filterModal.title')}
      footer={
        hasOnlyWholesale ? (
          <ModalFooter handleApply={handleApply} handleReset={handleReset} />
        ) : null
      }
      footerHeight={hasOnlyWholesale ? '130px' : '0px'}
    >
      <ModalContent
        values={values}
        selecteds={selecteds}
        handleChange={handleChange}
        handleChangeMetafields={handleChangeMetafields}
        handleReset={handleReset}
        handleApply={handleApply}
      />
    </ModalAside>
  );
}
